import type { Incident } from './types';

export const formatDuration = (seconds: number): string => {
    if (seconds < 60) return `${seconds} сек`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)} мин`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} ч`;
    return `${Math.floor(seconds / 86400)} дн`;
};

export const formatTimeAgo = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    
    if (diffSec < 5) return 'только что';
    if (diffSec < 60) return `${diffSec} сек назад`;
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)} мин назад`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)} ч назад`;
    return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
};

export const formatDateTime = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

export const calculateResolutionTime = (incident: Incident): number | null => {
    if (!incident.resolvedAt) return null;
    const created = new Date(incident.createdAt).getTime();
    const resolved = new Date(incident.resolvedAt).getTime();
    return Math.floor((resolved - created) / 60000); // в минутах
};

export const getSeverityLabel = (severity: number): string => {
    const labels: Record<number, string> = {
        1: 'Критический',
        2: 'Высокий',
        3: 'Средний',
        4: 'Низкий',
        5: 'Информационный'
    };
    return labels[severity] || 'Неизвестно';
};

export const generateMockIncidents = (): Incident[] => [
    {
        id: '1',
        type: 'critical',
        status: 'active',
        title: 'Магазин недоступен',
        description: 'Основной сайт электронной коммерции не отвечает на HTTP-запросы. Возможная причина — перегрузка сервера или сетевая проблема.',
        meta: {
            siteName: 'shop.example.ru',
            siteUrl: 'https://shop.example.ru',
            duration: 840,
            errorCode: '502 Bad Gateway',
            affectedUsers: 1240,
            region: 'Москва'
        },
        timeline: [
            {
                id: 't1',
                timestamp: new Date(Date.now() - 840000).toISOString(),
                type: 'created',
                message: 'Инцидент создан автоматически',
                author: 'System'
            },
            {
                id: 't2',
                timestamp: new Date(Date.now() - 800000).toISOString(),
                type: 'status_changed',
                message: 'Статус изменён на "Активен"',
                author: 'А.И.'
            }
        ],
        createdAt: new Date(Date.now() - 840000).toISOString(),
        updatedAt: new Date(Date.now() - 800000).toISOString(),
        assignedTo: 'А.И.',
        severity: 1
    },
    {
        id: '2',
        type: 'warning',
        status: 'investigating',
        title: 'Личный кабинет работает медленно',
        description: 'Время отклика API превысило пороговое значение (800 мс). Пользователи жалуются на медленную загрузку страниц.',
        meta: {
            siteName: 'lk.example.ru',
            siteUrl: 'https://lk.example.ru',
            duration: 1800,
            affectedUsers: 450,
            region: 'Все регионы'
        },
        timeline: [
            {
                id: 't1',
                timestamp: new Date(Date.now() - 1800000).toISOString(),
                type: 'created',
                message: 'Обнаружено замедление ответа API',
                author: 'System'
            }
        ],
        createdAt: new Date(Date.now() - 1800000).toISOString(),
        updatedAt: new Date(Date.now() - 1800000).toISOString(),
        assignedTo: 'Т.К.',
        severity: 2
    },
    {
        id: '3',
        type: 'resolved',
        status: 'resolved',
        title: 'API восстановлен после сбоя',
        description: 'Сервис API был недоступен в течение 3 минут. Причина — перезапуск контейнера после обновления.',
        meta: {
            siteName: 'api.example.ru',
            siteUrl: 'https://api.example.ru',
            duration: 180,
            region: 'Санкт-Петербург'
        },
        timeline: [
            {
                id: 't1',
                timestamp: new Date(Date.now() - 86400000).toISOString(),
                type: 'created',
                message: 'Обнаружен сбой API',
                author: 'System'
            },
            {
                id: 't2',
                timestamp: new Date(Date.now() - 86220000).toISOString(),
                type: 'resolved',
                message: 'Сервис автоматически восстановлен',
                author: 'System'
            }
        ],
        createdAt: new Date(Date.now() - 86400000).toISOString(),
        updatedAt: new Date(Date.now() - 86220000).toISOString(),
        resolvedAt: new Date(Date.now() - 86220000).toISOString(),
        severity: 3
    },
    {
        id: '4',
        type: 'info',
        status: 'resolved',
        title: 'Сертификат SSL истекает через 7 дней',
        description: 'Сертификат для домена example.ru истекает 25 марта 2026. Требуется продление.',
        meta: {
            siteName: 'example.ru',
            siteUrl: 'https://example.ru',
            region: 'Все регионы'
        },
        timeline: [
            {
                id: 't1',
                timestamp: new Date(Date.now() - 172800000).toISOString(),
                type: 'created',
                message: 'Уведомление о сроке сертификата',
                author: 'System'
            }
        ],
        createdAt: new Date(Date.now() - 172800000).toISOString(),
        updatedAt: new Date(Date.now() - 172800000).toISOString(),
        severity: 4
    }
];